import { retrieveFormById } from './api';
import { setFormDefaults } from './defaults';
import { enableAlwaysErrorMode } from './errors';
import { initializeExternalFormScripts } from './external-form-scripts';
import { initializeGeoAutocomplete } from './geo-autocomplete';
import { registerRecaptcha } from './recaptcha';
import { setSubmitListeners } from './submit';

export const renderForm = async (formDiv: Element, formId: number | string, formsUrl: string) => {
  // Set the HTML on the page
  // eslint-disable-next-line no-param-reassign
  formDiv.innerHTML = await retrieveFormById(formId, formsUrl);

  // Recreate the script so it can run in the browser
  const originalScripts = formDiv.querySelectorAll('script');
  originalScripts.forEach((originalScript) => {
    const script = document.createElement('script');
    script.innerHTML = originalScript.innerHTML;
    originalScript.parentNode.replaceChild(script, originalScript);
  });

  // The inline Gravity Forms bootstrap is injected after the original DOM-ready event.
  // Record the actual ready state before the dependency load events run.
  if (document.readyState !== 'loading' && window.gform) {
    window.gform.domLoaded = true;
  }

  await initializeExternalFormScripts(formsUrl);
  await initializeGeoAutocomplete(formDiv);

  // Adjust the HTML
  const form = formDiv.querySelector(`form[data-formid="${formId}"]`);
  if (!form) {
    return;
  }

  // Set the listeners
  setSubmitListeners(form);
  registerRecaptcha(formDiv, formId);
  setFormDefaults(formDiv);

  enableAlwaysErrorMode(formDiv);
  form.setAttribute('data-action', form.getAttribute('action') ?? '');
  form.removeAttribute('action');

  const submitButton = formDiv.querySelector('input[type="submit"], button[type="submit"]');
  submitButton?.removeAttribute('onkeypress');
  submitButton?.removeAttribute('onclick');

  // Force the form to display, even if Gravity Forms is hiding it.
  const gformWrapper = formDiv.querySelector('.gform_wrapper');
  if (gformWrapper) {
    (gformWrapper as HTMLElement).style.display = '';
  }

  // Force a re-render of conditional logic.
  // A setTimeout is used to ensure the form is fully rendered before the event is triggered.
  setTimeout(() => {
    triggerGravityFormsRenderedEvent(formId);
    triggerRenderedEvent(formId, formDiv, form);
  }, 0);
};

/**
 * Informs Gravity Forms that the form has been rendered. This will also re-trigger any of the conditional field logic.
 */
const triggerGravityFormsRenderedEvent = (formId: string | number) => {
  // Run after defaults have been applied and dependencies have finished loading.
  // Gravity Forms also dispatches the modern render events through this helper.
  if (window.gform?.core?.triggerPostRenderEvents) {
    window.gform.core.triggerPostRenderEvents(Number(formId), 1);
  } else {
    jQuery(document).trigger('gform_post_render', [Number(formId), 1]);
  }
};

/**
 * Dispatches a custom event to inform the page that the form has been rendered.
 * This is useful for any custom scripts that need to run after the form has been rendered.
 */
const triggerRenderedEvent = (formId: string | number, formDiv: Element, form: Element) => {
  const customEvent = new CustomEvent('leadFormRendered', {
    detail: {
      formId,
      formDiv,
      form,
    },
  });

  // Dispatch the custom event
  document.dispatchEvent(customEvent);
};

export const disableSubmitButton = (formDiv: Element) => {
  const form = formDiv.querySelector('form');
  const submitButton = form?.querySelector<HTMLInputElement | HTMLButtonElement>('input[type="submit"], button[type="submit"]');
  if (submitButton) submitButton.disabled = true;
};

export const enableSubmitButton = (formDiv: Element) => {
  const form = formDiv.querySelector('form');
  const submitButton = form?.querySelector<HTMLInputElement | HTMLButtonElement>('input[type="submit"], button[type="submit"]');
  if (submitButton) submitButton.disabled = false;
};
